<template>
    <div class="subBet notranslate">
        <template v-if="event && odds">
            <SportIcon
                v-bind:sportType="event.sportType"
                colorful
                size="lg"
                class="sport-icon"
            />
            <div class="subBet-info">
                <div class="subBet-option">
                    <strong>{{ optionName }}</strong>
                    <span class="separator">|</span>
                    <strong v-bind:class="isFavorite ? 'team-favorite' : ''"> {{ betOption }} </strong>
                    <strong v-if="pointDisplay" class="oddsPoint">{{ pointDisplay }}</strong>
                </div>
                <div class="subBet-match">
                    {{ homeTeamName }} -vs- {{ awayTeamName }}
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { useThrottleFn } from '@vueuse/core';
    import { storeToRefs } from 'pinia';
    import { computed, toRefs, watch } from 'vue';
    import { getTicketBetOptionDisplay } from '@/components/betSlip/ticketBetOptionDisplay';
    import SportIcon from '@/components/common/SportIcon.vue';
    import { MpCombinationContext } from '@/components/mpRecommendation/MpCombinationContext';
    import { defaultTitleFormatter } from '@/components/oddsDisplay/moreMarket/marketRules';
    import { useEventEmitter } from '@/composable/useEventEmitter';
    import { toFixedDecimalPoint } from '@/core/lib/numberFormat';
    import { isShowPoint, shouldNegatePoint } from '@/core/lib/oddsHelper';
    import { getTranslated } from '@/core/lib/translationHelper';
    import { useEventOdds } from '@/core/oddsApi/composable/useEventOdds';
    import { EnumOddsCategoryType, EnumPresetFilterType } from '@/core/oddsApi/oddsApiType';
    import type { ITicket } from '@/interface/IBetSlip';
    import type { IMpRecommendationEvent } from '@/interface/IEvent';
    import type { IMpRecommendationTicket, ISubBetKey } from '@/interface/IPopularMixParlay';
    import { AsiaBetPageType, OddsOption, PriceStyle } from '@/interface/enum';
    import { useCustomerStore } from '@/store/customerStore';

    const props = defineProps<{
        subBetKey: ISubBetKey;
        event: IMpRecommendationEvent;
    }>();

    const { event, subBetKey } = toRefs(props);

    const { priceStyle } = storeToRefs(useCustomerStore());

    const { oddsList, loaded: isOddsLoaded, refetch: refetchOdds, error: oddsError } = useEventOdds({
        eventId: subBetKey.value.eventId,
        oddsId: subBetKey.value.oddsId,
        presetFilter: EnumPresetFilterType.NonLive,
        oddsCategory: EnumOddsCategoryType.MixParlay,
        priceStyle,
        countBehavior: false,
        sportType: event.value.sportType,
        leagueSize: event.value.league.size,
        isSimulation: event.value.isSimulation,
    });

    const odds = computed(() => oddsList.value.find(odds => odds.id === subBetKey.value.oddsId));

    const { updateSubBet, removeMpCombination } = MpCombinationContext.inject();

    const subBet = computed<IMpRecommendationTicket | null>(() => {
        if (!odds.value) return null;

        const priceOption = odds.value.prices.find(price => price.option === subBetKey.value.option);
        if (!priceOption) return null;

        return {
            key: props.subBetKey.key,
            event: {
                id: event.value.id,
                homeTeam: {
                    name: event.value.homeTeam?.teamName,
                    liveScore: odds.value.eventResult.liveHomeScore ?? 0,
                },
                awayTeam: {
                    name: event.value.awayTeam?.teamName,
                    liveScore: odds.value.eventResult.liveAwayScore ?? 0,
                },
                eventResults: [event.value.mainMarketEventResult],
                league: {
                    id: event.value.league.id,
                    name: event.value.league.name,
                    size: event.value.league.size,
                },
                sportType: event.value.sportType,
                isLive: event.value.isLive,
                isSimulation: event.value.isSimulation,
            },
            odds: odds.value,
            priceOption: {
                option: priceOption.option,
                price: priceOption.price,
            },
        };
    });

    const betOptionDisplay = computed(() => {
        if (!subBet.value) return null;

        if (!subBet.value.event || !subBet.value.odds || !subBet.value.priceOption) {
            return null;
        }

        const ticket: ITicket = {
            key: subBet.value.key,
            betPage: AsiaBetPageType.MpRecommendation,
            event: subBet.value.event,
            odds: subBet.value.odds,
            priceStyle: PriceStyle.Euro,
            priceOption: subBet.value.priceOption,
        };

        return getTicketBetOptionDisplay(ticket);
    });

    const isFavorite = computed(() => betOptionDisplay.value?.isFavorite ?? false);

    const homeTeamName = computed(() => getTranslated(event.value?.homeTeam?.teamName));
    const awayTeamName = computed(() => getTranslated(event.value?.awayTeam?.teamName));

    const pointDisplay = computed(() => {
        if (!odds.value) {
            return null;
        }

        if (isShowPoint(odds.value.marketType, odds.value.eventResult.marketGroup.id)) {
            const oddsOption = OddsOption.fromOddsApi(subBetKey.value.option, odds.value.marketType);
            const point = shouldNegatePoint(odds.value.point, odds.value.marketType, oddsOption)
                ? -odds.value.point
                : odds.value.point;
            return toFixedDecimalPoint(point, 2);
        }

        return null;
    });

    const betOption = computed(() => {
        if (!subBet.value) return '';
        return betOptionDisplay.value?.name ?? props.subBetKey.option;
    });

    const optionName = computed(() => {
        if (!event.value || !odds.value) return '';
        return defaultTitleFormatter(event.value, odds.value);
    });

    watch([isOddsLoaded, odds], () => {
        if (!isOddsLoaded.value) {
            return;
        }
        if (!odds.value) {
            removeMpCombination();
        }
    });

    watch(subBet, () => {
        if (subBet.value) {
            updateSubBet(subBet.value.key, subBet.value);
        }
    }, { immediate: true });

    watch(oddsError, () => {
        if (oddsError.value) {
            removeMpCombination();
        }
    });

    const eventEmitter = useEventEmitter();
    const throttledRefetchOdds = useThrottleFn(refetchOdds, 5000, false, true);
    eventEmitter.on('oddsDisplay:refreshMpRecommendation', () => {
        throttledRefetchOdds();
    });
</script>

<style lang="scss" scoped>
    @import "@/components/mpRecommendation/MpCombinationSubBet";
</style>
